/**
 * Date Utility Functions for Bulk Events
 * 
 * Provides date calculation and formatting utilities for generating
 * recurring event schedules.
 */

/**
 * Get the first Monday of July in the specified year
 * @param year - The year to calculate for
 * @returns Date object representing the first Monday of July
 */
export function getFirstMondayOfJuly(year: number): Date {
  const july1 = new Date(year, 6, 1); // Month is 0-indexed (6 = July)
  const dayOfWeek = july1.getDay(); // 0 = Sunday, 1 = Monday, etc.
  
  // Calculate days until next Monday
  // If July 1 is Sunday (0), add 1 day
  // If July 1 is Monday (1), add 0 days
  // If July 1 is Tuesday-Saturday (2-6), add (8 - dayOfWeek) days
  const daysUntilMonday = dayOfWeek === 0 ? 1 : dayOfWeek === 1 ? 0 : (8 - dayOfWeek);
  
  return addDays(july1, daysUntilMonday);
}

/**
 * Get the first Monday of November in the specified year
 * @param year - The year to calculate for
 * @returns Date object representing the first Monday of November
 */
export function getFirstMondayOfNovember(year: number): Date {
  const nov1 = new Date(year, 10, 1); // Month is 0-indexed (10 = November)
  const dayOfWeek = nov1.getDay();
  
  const daysUntilMonday = dayOfWeek === 0 ? 1 : dayOfWeek === 1 ? 0 : (8 - dayOfWeek);
  
  return addDays(nov1, daysUntilMonday);
}

/**
 * Get the first Monday of the week containing the given date
 * Treats Monday as the first day of the week
 * @param date - The date to find the week start for
 * @returns Date object representing the Monday of that week
 */
export function getFirstMondayOfWeek(date: Date): Date {
  const dayOfWeek = date.getDay(); // 0 = Sunday, 1 = Monday, etc.
  
  // Calculate days to subtract to get to Monday
  // If Sunday (0), subtract 6 days to get to previous Monday
  // If Monday (1), subtract 0 days
  // If Tuesday-Saturday (2-6), subtract (dayOfWeek - 1) days
  const daysToSubtract = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
  
  return addDays(date, -daysToSubtract);
}

/**
 * Add a specified number of days to a date
 * @param date - The starting date
 * @param days - Number of days to add (can be negative)
 * @returns New Date object with days added
 */
export function addDays(date: Date, days: number): Date {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
}

/**
 * Add a specified number of weeks to a date
 * @param date - The starting date
 * @param weeks - Number of weeks to add (can be negative)
 * @returns New Date object with weeks added
 */
export function addWeeks(date: Date, weeks: number): Date {
  return addDays(date, weeks * 7);
}

/**
 * Format a date for use in HTML date input (YYYY-MM-DD)
 * @param date - The date to format
 * @returns String in YYYY-MM-DD format
 */
export function formatDateForInput(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Format a date for display (e.g., "January 15, 2024")
 * @param date - The date to format
 * @returns Formatted date string
 */
export function formatDateForDisplay(date: Date): string {
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

/**
 * Format a datetime for display (e.g., "January 15, 2024 at 6:30 PM")
 * @param isoString - ISO datetime string
 * @returns Formatted datetime string
 */
export function formatDateTimeForDisplay(isoString: string): string {
  const date = new Date(isoString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}
